import { ExternalLink, Info } from "lucide-react";

/**
 * Orientačný cenník zatepľovacích prác — dáta prevzaté zo stavebne-dopyty.sk
 * (sekcia zateplenie, ceny 2026 vrátane práce). Zdroj je uvedený a odkazovaný.
 */
const FACADE = [
  { name: "EPS 10 cm", note: "bežný fasádny polystyrén", price: "28 – 40 €/m²" },
  { name: "EPS Grafit 10 cm", note: "grafitový, lepší tepelný odpor (λ)", price: "32 – 45 €/m²" },
  { name: "Minerálna vlna 10 cm", note: "nehorľavá, paropriepustná", price: "40 – 55 €/m²" },
  { name: "Nad 12 cm hrúbky", note: "za každé ďalšie 2 cm", price: "+3 – 6 €/m²" },
];

const ROOF = [
  { name: "Minerálna vlna 20 cm", note: "medzikrokvová izolácia", price: "15 – 25 €/m²" },
  { name: "Fúkaná celulóza 25 cm", note: "fúkaná izolácia", price: "18 – 30 €/m²" },
  { name: "PIR panely 15 cm", note: "nadkrokvová izolácia", price: "35 – 55 €/m²" },
];

function PriceTable({ title, rows, testid }) {
  return (
    <div className="border border-white/10 bg-ink-2" data-testid={testid}>
      <div className="px-6 py-4 border-b border-white/10 font-mono2 text-xs uppercase tracking-widest text-gold">{title}</div>
      <div className="divide-y divide-white/5">
        {rows.map((r) => (
          <div key={r.name} className="flex items-center justify-between gap-4 px-6 py-4">
            <div>
              <div className="text-white text-sm font-semibold">{r.name}</div>
              <div className="text-white/45 text-xs mt-0.5">{r.note}</div>
            </div>
            <div className="font-display text-lg md:text-xl text-gold whitespace-nowrap">{r.price}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function WorkPricing() {
  return (
    <section id="cennik-prac" className="bg-ink py-20 md:py-28 border-t border-white/10" data-testid="work-pricing">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Cenník prác · 2026</p>
        <h2 className="font-display text-4xl md:text-6xl leading-[0.95] text-white max-w-3xl">
          Orientačný cenník <span className="text-gold">zatepľovacích prác</span>
        </h2>
        <p className="text-white/50 text-sm md:text-base mt-4 max-w-2xl">
          Trhové ceny zateplenia za m² vrátane práce (fasáda ETICS aj strecha). Presnú cenu pre váš dom
          vypočíta naša <a href="/#kalkulacka" className="text-gold hover:underline">kalkulačka</a>.
        </p>

        <div className="mt-6 border-l-4 border-gold bg-gold/5 px-5 py-4 max-w-3xl" data-testid="work-pricing-note">
          <p className="text-white/70 text-sm leading-relaxed">
            <span className="text-gold font-semibold">Pozor:</span> ceny 28–55 €/m² sú orientačné ceny konkrétnych
            materiálových systémov a prác. Cena <strong className="text-white">od 55 €/m² na kľúč</strong> zahŕňa
            kompletnú realizáciu vrátane materiálu, práce, lešenia a dopravy.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-12">
          <PriceTable title="Fasáda (ETICS) — s prácou" rows={FACADE} testid="price-facade" />
          <PriceTable title="Strecha — podľa materiálu" rows={ROOF} testid="price-roof" />
        </div>

        <div className="flex items-start gap-3 mt-8 text-white/45 text-xs max-w-3xl">
          <Info className="w-4 h-4 text-gold shrink-0 mt-0.5" />
          <p>
            Ceny sú orientačné (rok 2026) a závisia od rozsahu, stavu fasády, lokality a termínu. Na zateplenie
            rodinného domu je možné čerpať štátnu dotáciu z programu Obnov dom (Plán obnovy) — žiadosť sa podáva
            pred začatím prác; aktuálne podmienky overte na oficiálnej stránke. Zdroj trhových cien:{" "}
            <a href="https://stavebne-dopyty.sk/zateplenie-domu-cena" target="_blank" rel="noreferrer"
               className="text-gold hover:underline inline-flex items-center gap-1" data-testid="work-pricing-source">
              Stavebné-dopyty.sk <ExternalLink className="w-3 h-3" />
            </a>.
          </p>
        </div>
      </div>
    </section>
  );
}
